import { DEFAULT_CURRENCY } from "@redotech/money/util";
import { attributeDefinitionIdFormat } from "@redotech/redo-ids/ids";
import {
  AttributeType,
  AttributeValueSchema,
  AttributeValueType,
  createSafeNullableEnumSchema,
  MeasurementSchema,
  MoneySchema,
} from "@redotech/redo-inventory-common/attributes";
import { ChatGptVariantFieldType } from "@redotech/redo-prisma-client/enums";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { BaseProductListingSchema, ListingType } from "../base-listing";
import { ChatGptListingMediaSchema } from "./chatgpt-media";

export enum SizeSystem {
  US = "US",
  UK = "UK",
  EU = "EU",
  AU = "AU",
  BR = "BR",
  CN = "CN",
  DE = "DE",
  FR = "FR",
  IT = "IT",
  JP = "JP",
  MEX = "MEX",
}
export const SizeSystemSchema = createEnum(SizeSystem);

export enum Gender {
  Male = "male",
  Female = "female",
  Unisex = "unisex",
}
export const GenderSchema = createEnum(Gender);

export const ChatGptVariantListingSchema_ = BaseProductListingSchema.extend({
  listingType: z.literal(ListingType.ChatGpt),
  id: z.bigint(),
  sku: z.string().nullable().default(""),
  price: MoneySchema.nullable().default({
    amount: 0,
    currency: DEFAULT_CURRENCY,
  }),
  salePrice: MoneySchema.nullable(),
  salePriceEffectiveDate: z.date().nullable(),
  unitPricingMeasure: MeasurementSchema.nullable(),
  baseMeasure: MeasurementSchema.nullable(),
  pricingTrend: z.string().nullable(),
  color: z.string().nullable(),
  size: z.string().nullable(),
  sizeSystem: SizeSystemSchema.nullable(),
  gender: GenderSchema.nullable(),
  offerId: z.string().nullable(),
  customVariant1Category: z.string().nullable(),
  customVariant1Value: z.string().nullable(),
  customVariant2Category: z.string().nullable(),
  customVariant2Value: z.string().nullable(),
  customVariant3Category: z.string().nullable(),
  customVariant3Value: z.string().nullable(),
  media: z.array(ChatGptListingMediaSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const ChatGptVariantListingSchema: z.ZodType<ChatGptVariantListing> =
  ChatGptVariantListingSchema_;
export interface ChatGptVariantListing extends z.infer<
  typeof ChatGptVariantListingSchema_
> {}

export const ChatGptVariantFieldTypeSchema = createEnum(
  ChatGptVariantFieldType,
);
const ChatGptVariantMappingSchema_ = z.object({
  fieldName: ChatGptVariantFieldTypeSchema,
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const ChatGptVariantMappingSchema: z.ZodType<ChatGptVariantMapping> =
  ChatGptVariantMappingSchema_;
export interface ChatGptVariantMapping extends z.infer<
  typeof ChatGptVariantMappingSchema_
> {}

export const ChatGptVariantAttributeType = {
  [ChatGptVariantFieldType.sku]: AttributeType.ShortText,
  [ChatGptVariantFieldType.price]: AttributeType.Money,
  [ChatGptVariantFieldType.sale_price]: AttributeType.Money,
  [ChatGptVariantFieldType.sale_price_effective_date]: AttributeType.Date,
  [ChatGptVariantFieldType.unit_pricing_measure]: AttributeType.Dimension,
  [ChatGptVariantFieldType.base_measure]: AttributeType.Dimension,
  [ChatGptVariantFieldType.pricing_trend]: AttributeType.ShortText,
  [ChatGptVariantFieldType.color]: AttributeType.Color,
  [ChatGptVariantFieldType.size]: AttributeType.ShortText,
  [ChatGptVariantFieldType.size_system]: AttributeType.Enum,
  [ChatGptVariantFieldType.gender]: AttributeType.Enum,
  [ChatGptVariantFieldType.offer_id]: AttributeType.ShortText,
  [ChatGptVariantFieldType.custom_variant1_category]: AttributeType.ShortText,
  [ChatGptVariantFieldType.custom_variant1_value]: AttributeType.ShortText,
  [ChatGptVariantFieldType.custom_variant2_category]: AttributeType.ShortText,
  [ChatGptVariantFieldType.custom_variant2_value]: AttributeType.ShortText,
  [ChatGptVariantFieldType.custom_variant3_category]: AttributeType.ShortText,
  [ChatGptVariantFieldType.custom_variant3_value]: AttributeType.ShortText,
} satisfies Record<ChatGptVariantFieldType, AttributeType>;

export const ChatGptVariantAttributeSchema = {
  [ChatGptVariantFieldType.sku]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatGptVariantFieldType.sku]
    ].nullable(),
  [ChatGptVariantFieldType.price]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatGptVariantFieldType.price]
    ].nullable(),
  [ChatGptVariantFieldType.sale_price]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatGptVariantFieldType.sale_price]
    ].nullable(),
  [ChatGptVariantFieldType.sale_price_effective_date]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[
        ChatGptVariantFieldType.sale_price_effective_date
      ]
    ].nullable(),
  [ChatGptVariantFieldType.unit_pricing_measure]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatGptVariantFieldType.unit_pricing_measure]
    ].nullable(),
  [ChatGptVariantFieldType.base_measure]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatGptVariantFieldType.base_measure]
    ].nullable(),
  [ChatGptVariantFieldType.pricing_trend]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatGptVariantFieldType.pricing_trend]
    ].nullable(),
  [ChatGptVariantFieldType.color]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatGptVariantFieldType.color]
    ].nullable(),
  [ChatGptVariantFieldType.size]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatGptVariantFieldType.size]
    ].nullable(),
  [ChatGptVariantFieldType.size_system]:
    createSafeNullableEnumSchema(SizeSystemSchema),
  [ChatGptVariantFieldType.gender]: createSafeNullableEnumSchema(GenderSchema),
  [ChatGptVariantFieldType.offer_id]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatGptVariantFieldType.offer_id]
    ].nullable(),
  [ChatGptVariantFieldType.custom_variant1_category]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[
        ChatGptVariantFieldType.custom_variant1_category
      ]
    ].nullable(),
  [ChatGptVariantFieldType.custom_variant1_value]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatGptVariantFieldType.custom_variant1_value]
    ].nullable(),
  [ChatGptVariantFieldType.custom_variant2_category]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[
        ChatGptVariantFieldType.custom_variant2_category
      ]
    ].nullable(),
  [ChatGptVariantFieldType.custom_variant2_value]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatGptVariantFieldType.custom_variant2_value]
    ].nullable(),
  [ChatGptVariantFieldType.custom_variant3_category]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[
        ChatGptVariantFieldType.custom_variant3_category
      ]
    ].nullable(),
  [ChatGptVariantFieldType.custom_variant3_value]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatGptVariantFieldType.custom_variant3_value]
    ].nullable(),
};

export type ChatGptVariantAttributeValueType = {
  [key in ChatGptVariantFieldType]: (typeof ChatGptVariantAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getChatGptVariantAttributeValue<
  T extends ChatGptVariantFieldType,
>(field: T, value: unknown): ChatGptVariantAttributeValueType[T] | null {
  return ChatGptVariantAttributeSchema[field].parse(
    value,
  ) as ChatGptVariantAttributeValueType[T];
}

export function getChatGptVariantAttributeType<
  T extends ChatGptVariantFieldType,
>(field: T): (typeof ChatGptVariantAttributeType)[T] {
  return ChatGptVariantAttributeType[field];
}

export function getChatGptVariantListingAttributeValue<
  T extends ChatGptVariantFieldType,
>({
  listing,
  field,
}: {
  listing: ChatGptVariantListing;
  field: T;
}): AttributeValueType[AttributeType] | null {
  let fieldValue;
  switch (field) {
    case "sku":
      fieldValue = listing.sku;
      break;
    case "price":
      fieldValue = listing.price;
      break;
    case "sale_price":
      fieldValue = listing.salePrice;
      break;
    case "sale_price_effective_date":
      fieldValue = listing.salePriceEffectiveDate;
      break;
    case "unit_pricing_measure":
      fieldValue = listing.unitPricingMeasure;
      break;
    case "base_measure":
      fieldValue = listing.baseMeasure;
      break;
    case "pricing_trend":
      fieldValue = listing.pricingTrend;
      break;
    case "color":
      fieldValue = listing.color;
      break;
    case "size":
      fieldValue = listing.size;
      break;
    case "size_system":
      fieldValue = listing.sizeSystem;
      break;
    case "gender":
      fieldValue = listing.gender;
      break;
    case "offer_id":
      fieldValue = listing.offerId;
      break;
    case "custom_variant1_category":
      fieldValue = listing.customVariant1Category;
      break;
    case "custom_variant1_value":
      fieldValue = listing.customVariant1Value;
      break;
    case "custom_variant2_category":
      fieldValue = listing.customVariant2Category;
      break;
    case "custom_variant2_value":
      fieldValue = listing.customVariant2Value;
      break;
    case "custom_variant3_category":
      fieldValue = listing.customVariant3Category;
      break;
    case "custom_variant3_value":
      fieldValue = listing.customVariant3Value;
      break;
    default:
      assertNever(field);
  }
  return ChatGptVariantAttributeSchema[field].parse(fieldValue);
}
